import { useNavigate, useParams } from "react-router-dom";
import useDocumentTitle from "../../util/useDocumentTitle";
import { selectCurrentUser } from "../../redux/authentication/authSlice";
import { useSelector } from "react-redux";
import { UserGroup } from "../../common/User";
import { useEffect, useState } from "react";
import { useGetVesselQuery, useGetVesselsQuery } from "../../redux/api/vessel";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { toast } from "react-toastify";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  CircularProgress,
  TextField,
} from "@mui/material";
import { colorFromName } from "../../common/ColorFromName";
import { Vessel } from "../../common/Vessel";
import TrainingRosterChangeForm from "./TrainingRosterChangeForm";

const TrainingRosterComponent = () => {
  useDocumentTitle("Training Roster");
  const { id } = useParams();
  const navigate = useNavigate();

  const user = useSelector(selectCurrentUser);
  const isViewOnly =
    (user?.userGroup as UserGroup) === UserGroup.VESSEL_EXECS ||
    (user?.userGroup as UserGroup) === UserGroup.FLEET_EXECS;

  const [rosterFormKey, setRosterFormKey] = useState("a");
  const [changedRowsWithError, setChangedRowsWithError] = useState<number[]>(
    []
  );
  const [formSubmitted, setFormSubmitted] = useState(false);
  const { data: vesselOptions, isLoading, isFetching } = useGetVesselsQuery({});
  const { data: vessel } = useGetVesselQuery(
    id !== undefined && id !== "" && id !== "new" ? id : skipToken
  );

  const changeVessel = (vesselId: number | undefined) => {
    if (vesselId != null) {
      navigate(`/trainingRoster/${vesselId}`);
    }
  };

  useEffect(() => {
    if (vesselOptions?.length === 1) {
      changeVessel(vesselOptions[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vesselOptions]);

  const handleCallback = (saveResult: {
    data: {
      successCount: number;
      failedCount: number;
      additionalError?: string;
    };
    hasData: boolean;
    errors: number[];
    hasError: boolean;
    hasInfo: boolean;
  }) => {
    if (saveResult.hasData && !!vessel?.id) {
      setRosterFormKey(`${rosterFormKey} === a ? b : a`);
      if (saveResult.data.successCount > 0) {
        toast.success(
          <p>
            Changes for {saveResult.data.successCount} record(s) saved
            successfully.
          </p>,
          {
            position: "top-center",
            autoClose: 5000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "colored",
          }
        );
      }
      if (saveResult.data.failedCount > 0) {
        toast.error(
          <p>
            Changes for {saveResult.data.failedCount} could not be saved.
            {!!saveResult.data.additionalError &&
              " " + saveResult.data.additionalError}
          </p>,
          {
            position: "top-center",
            autoClose: !!saveResult.data.additionalError ? false : 5000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "colored",
          }
        );
      }
    }
    if (saveResult.hasError) {
      setChangedRowsWithError(saveResult.errors);
      toast.error(<p>Required fields are missing.</p>, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
    }
    if (saveResult.hasInfo) {
      setChangedRowsWithError([]);
      toast.info(<p>No Job Order changes to save.</p>, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
    }
    setFormSubmitted(false);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {!isLoading && !isFetching && (
        <>
          <div className="roster-page-header mb-0">
            <div className="header-group">
              <h1>Training Roster</h1>
              <Avatar
                sx={{
                  bgcolor: user ? colorFromName(user.name) : colorFromName(""),
                  height: 24,
                  width: 24,
                  fontSize: 16,
                }}
                alt={user ? user.name : ""}
                src="/"
              />
              {!!vesselOptions?.length && vesselOptions?.length === 1 && (
                <h3>{vessel?.name}</h3>
              )}
              {!!vesselOptions?.length && vesselOptions?.length > 1 && (
                <Autocomplete
                  className="vessel-dropdown"
                  id="vesselOptions"
                  options={
                    vesselOptions
                      ? (vesselOptions as Vessel[]).filter((v) => !v.disabled)
                      : []
                  }
                  value={vessel ? vessel : null}
                  onChange={(event, value) => changeVessel(value?.id)}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Pick a Vessel" />
                  )}
                  isOptionEqualToValue={(opt, val) => opt.id === val.id}
                  getOptionLabel={(opt) => (opt.name ? opt.name : "")}
                />)}
              <div>
                {!isViewOnly && (
                  <Button
                    form="trainingRosterForm"
                    variant="contained"
                    className="blue-button wd-auto ht-4px"
                    type="submit"
                    disabled={formSubmitted}
                  >
                    {" "}
                    {formSubmitted === true ? (
                      <CircularProgress className="flex align-items-center color-white wd-2px" />
                    ) : (
                      "Save Changes"
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
          <TrainingRosterChangeForm
            parentCallback={handleCallback}
            key={rosterFormKey}
            changedRowsWithError={changedRowsWithError}
            formSubmitted={formSubmitted}
            setFormSubmitted={setFormSubmitted}
          />
        </>
      )}
    </>
  );
};

export default TrainingRosterComponent;
